import React, { useEffect, useState, useCallback } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import Navbar from "@/components/Navbar";
import Countdown from "@/components/Countdown";
import Reveal from "@/components/Reveal";
import StoryCarousel from "@/components/StoryCarousel";
import { DressCodeIcon, RegistryIcon, HotelIcon, AdultsOnlyIcon } from "@/components/Illustrations";
import { MapPin, ChevronDown, X, ChevronLeft, ChevronRight, Plane, Car, Wallet, CloudSun, Languages, Info, Phone, HeartHandshake, Sparkles, Hotel } from "lucide-react";

const HERO_BG_IMG =
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/qvmlgcuy_AleyEdfinal.png";

const GALLERY_REAL = [
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/d93q64ef_IMG_6744%202.webp",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/91idaclv_IMG_6689.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/5srp4i4u_IMG_0400.webp",
  "/gallery/grotto.webp",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/owroae4c_howthaye.webp",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/u9slwyc8_IMG_7748.webp",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/3wj8ffqv_IMG_1439.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/o5llw9py_IMG_5976.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/b4ckf8uc_IMG_9657.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/exlhz7ox_IMG_4598.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/z941lvbn_IMG_9110.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/nwkeyxhf_IMG_3096.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/rbqjlrif_IMG_7502.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/99e4slll_IMG_3038.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/kzms9ise_IMG_2415.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/f8gbj2o9_IMG_5910.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/cdte95zu_IMG_5198.jpeg",
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/hhhc9x5a_IMG_2204.jpeg",
  "/gallery/tequila.webp",
  "/gallery/ayeMex.webp",
];

const CEREMONY_IMG =
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/cydq6kbi_Timeline-Mass.png";

const COCKTAIL_IMG =
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/ymfcz650_Timeline_Cocktail.png";

const DINNER_IMG =
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/lw7tfbmv_Timeline_Dinner.png";

const ENDOFPARTY_IMG =
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/bzpdtz3q_Timeline_EndOfParty.png";

const TIMELINE_IMGS = [CEREMONY_IMG, COCKTAIL_IMG, DINNER_IMG, ENDOFPARTY_IMG];

const STORY_IMG =
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/e8gzr03l_bitmap.png";

const VENUE_IMG =
  "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/x1htv9cy_Venue-1.jpg";

const GALLERY_IMGS = GALLERY_REAL;

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Hacienda+Benazuza+Zapopan+Jalisco";
const MAPS_EMBED =
  "https://www.google.com/maps?q=Hacienda+Benazuza+Zapopan+Jalisco&output=embed";

const SectionEyebrow = ({ children }) => (
  <div className="flex items-center justify-center gap-4 mb-5">
    <span className="ornament" />
    <span className="eyebrow">{children}</span>
    <span className="ornament" />
  </div>
);

const Home = () => {
  const { t } = useLanguage();
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const prevImg = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i - 1 + GALLERY_IMGS.length) % GALLERY_IMGS.length)),
    [],
  );
  const nextImg = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i + 1) % GALLERY_IMGS.length)),
    [],
  );

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevImg();
      else if (e.key === "ArrowRight") nextImg();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxIdx, closeLightbox, prevImg, nextImg]);

  return (
    <div data-testid="home-page" className="App">
      <Navbar />

      {/* HERO */}
      <section
        id="hero"
        data-testid="hero-section"
        className="relative h-screen min-h-[680px] w-full overflow-hidden"
      >
        {/* Background layer: couple at Lake Como */}
        <div
          className="absolute inset-0 bg-cover bg-center hero-zoom"
          style={{
            backgroundImage: `url(${HERO_BG_IMG})`,
            filter: "saturate(1.08) contrast(1.05) brightness(1.02)",
          }}
        />
        {/* Subtle dark wash so the names read */}
        <div className="absolute inset-0 bg-black/30" />
        {/* Final gradient for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/60" />

        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col items-center justify-center text-center text-white">
          <Reveal>
            <p data-testid="hero-eyebrow" className="sr-only">
              {t.hero.saveTheDate}
            </p>
          </Reveal>

          <Reveal delay={150}>
            <h1 className="font-script text-6xl md:text-8xl lg:text-9xl leading-none">
              Alejandra
            </h1>
            <div className="my-4 md:my-6 font-serif italic text-2xl md:text-4xl text-[var(--accent)]">
              {t.hero.and}
            </div>
            <h1 className="font-script text-6xl md:text-8xl lg:text-9xl leading-none">
              Edouard
            </h1>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-10 mb-12 flex flex-col items-center gap-3">
              <div className="hairline w-24 bg-white/40" />
              <p data-testid="hero-date" className="font-serif text-xl md:text-2xl tracking-wide">
                {t.hero.date}
              </p>
              <p data-testid="hero-location" className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-white/80">
                Guadalajara, Jalisco · México
              </p>
            </div>
          </Reveal>

          <Reveal delay={450}>
            <Countdown />
          </Reveal>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/70">
            <span className="text-[10px] tracking-[0.3em] uppercase">scroll</span>
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </div>
        </div>
      </section>

      {/* WELCOME MESSAGE */}
      <section id="welcome" data-testid="welcome-section" className="py-20 md:py-28 px-6 md:px-10 bg-[var(--ivory)]">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <SectionEyebrow>{t.welcome.eyebrow}</SectionEyebrow>
            <h2 className="section-title mb-10">{t.welcome.title}</h2>
            <div className="hairline w-12 bg-[var(--sage-deep)] mx-auto mb-10" />
            <div className="space-y-5 text-[var(--text-secondary)] leading-[1.9] text-base md:text-lg font-light">
              {t.welcome.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <p className="font-serif italic text-lg md:text-xl text-[var(--text-primary)] mt-10 whitespace-pre-line">
              {t.welcome.signature}
            </p>
          </Reveal>
        </div>
      </section>

      {/* OUR STORY */}
      <section id="story" data-testid="story-section" className="pt-16 md:pt-24 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center mb-4 md:mb-6">
          <Reveal>
            <SectionEyebrow>{t.story.eyebrow}</SectionEyebrow>
            <h2 className="section-title">{t.story.title}</h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 items-center">
          <Reveal className="w-full min-w-0">
            <img
              src={STORY_IMG}
              alt="Alejandra and Edouard"
              className="block w-full h-[55vh] md:h-auto object-cover object-top md:object-center md:w-[50vw]"
              style={{ mixBlendMode: "multiply" }}
            />
          </Reveal>
          <Reveal className="w-full min-w-0">
            <div className="px-6 md:px-16 lg:px-24 py-8 md:py-10 text-center w-full min-w-0">
              <div className="hairline w-16 bg-[var(--sage-deep)] mb-6 mx-auto" />

              {/* Desktop: stacked paragraphs */}
              <div className="hidden md:block space-y-4 text-[var(--text-secondary)] leading-[1.85] text-base md:text-lg font-light">
                {t.story.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              {/* Mobile: swipeable card carousel */}
              <StoryCarousel paragraphs={t.story.body} />

              <p className="font-serif italic text-lg text-[var(--text-primary)] mt-8">
                {t.story.signature}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ITINERARY */}
      <section id="itinerary" data-testid="itinerary-section" className="py-24 md:py-36 px-6 md:px-10 bg-[var(--ivory)]">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <SectionEyebrow>{t.itinerary.eyebrow}</SectionEyebrow>
            <h2 className="section-title mb-16">{t.itinerary.title}</h2>
          </Reveal>

          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-1/2 top-2 bottom-2 w-px bg-[var(--sage-soft)] -translate-x-1/2 hidden md:block" />
            <div className="space-y-6 md:space-y-8">
              {t.itinerary.events.map((ev, i) => {
                const isLeft = i % 2 === 0;
                const img = TIMELINE_IMGS[i];
                return (
                  <Reveal key={i} delay={i * 100}>
                    <div className="md:grid md:grid-cols-2 md:gap-12 md:items-center relative">
                      <div className={`text-center md:text-inherit ${isLeft ? "md:text-right md:pr-8" : "md:order-2 md:text-left md:pl-8"}`}>
                        <p className="eyebrow mb-3">{ev.time}</p>
                        <h3 className="font-serif text-2xl md:text-3xl text-[var(--text-primary)] mb-2">
                          {ev.title}
                        </h3>
                        <p className="text-sm text-[var(--sage-deep)] tracking-wider uppercase">
                          {ev.place}
                        </p>
                      </div>
                      <div className={`${isLeft ? "" : "md:order-1"}`}>
                        {img && (
                          <div className={`flex justify-center mt-6 md:mt-0 ${isLeft ? "md:justify-start" : "md:justify-end"}`}>
                            <img
                              src={img}
                              alt={ev.title}
                              className="w-44 md:w-56 lg:w-64 h-auto"
                              data-testid={`timeline-img-${i}`}
                            />
                          </div>
                        )}
                      </div>
                      <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center justify-center pointer-events-none">
                        <div className="w-3 h-3 rounded-full bg-[var(--sage-deep)] ring-4 ring-[var(--ivory)]" />
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={150}>
              <div
                data-testid="unplugged-ceremony-notice"
                className="mt-14 md:mt-16 max-w-2xl mx-auto text-center border border-[var(--border-soft)] bg-white px-8 py-8 md:px-12 md:py-10"
              >
                <p className="eyebrow font-bold mb-4 text-[var(--sage-deep)]">
                  {t.itinerary.unplugged.label}
                </p>
                <p className="text-sm md:text-[15px] text-[var(--text-secondary)] leading-relaxed font-light mb-4">
                  {t.itinerary.unplugged.body}
                </p>
                <p className="text-sm md:text-[15px] text-[var(--text-secondary)] leading-relaxed font-light mb-4">
                  {t.itinerary.unplugged.photographers}
                </p>
                <p className="font-serif italic text-[var(--text-primary)] text-base md:text-lg">
                  {t.itinerary.unplugged.after}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VENUE */}
      <section id="venue" data-testid="venue-section" className="py-24 md:py-36 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center mb-14">
              <SectionEyebrow>{t.venue.eyebrow}</SectionEyebrow>
              <h2 className="section-title mb-6">{t.venue.title}</h2>
              <p className="text-[var(--text-secondary)] max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                {t.venue.address}
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={150}>
          <div
            className="w-full h-64 md:h-[480px] bg-cover bg-center mb-12"
            style={{ backgroundImage: `url(${VENUE_IMG})` }}
          />
        </Reveal>

        <div className="max-w-6xl mx-auto px-6 md:px-10 mb-14">
          <Reveal delay={100}>
            <div
              data-testid="venue-parking"
              className="max-w-3xl mx-auto text-center border border-[var(--border-soft)] bg-[var(--ivory)] px-8 py-8 md:px-12 md:py-10"
            >
              <p className="eyebrow mb-3 text-[var(--sage-deep)]">{t.venue.parkingLabel}</p>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed font-light">
                {t.venue.parkingNote}
              </p>
            </div>
          </Reveal>
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Reveal delay={150}>
            <div className="max-w-3xl mx-auto aspect-[16/9] overflow-hidden border border-[var(--border-soft)] relative">
              <iframe
                data-testid="venue-map"
                title="map"
                src={MAPS_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              {/* Overlay to block clicks on Google reviews link inside the info card */}
              <div
                data-testid="venue-map-reviews-block"
                aria-hidden="true"
                className="absolute left-3 top-[92px] w-56 h-8 z-10 bg-transparent"
                style={{ pointerEvents: "auto" }}
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* DETAILS */}
      <section id="details" data-testid="details-section" className="py-24 md:py-36 px-6 md:px-10 bg-[var(--ivory)]">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <SectionEyebrow>{t.details.eyebrow}</SectionEyebrow>
              <h2 className="section-title">{t.details.title}</h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8">
            <Reveal>
              <div className="p-10 border border-[var(--border-soft)] h-full text-center bg-white">
                <DressCodeIcon className="w-16 h-16 mx-auto mb-5 text-[var(--sage-deep)]" />
                <p className="eyebrow mb-3">{t.details.dressCode.title}</p>
                <h3 className="font-serif text-2xl text-[var(--text-primary)] mb-4">
                  {t.details.dressCode.value}
                </h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-5 whitespace-pre-line">
                  {t.details.dressCode.note}
                </p>
                <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--sage-deep)] mb-4">
                  {t.details.dressCode.avoid}
                </p>
                <div className="flex justify-center items-center pl-3">
                  {t.details.dressCode.avoidPalette.map((c) => (
                    <span
                      key={c.name}
                      data-testid={`avoid-color-${c.name}`}
                      title={c.name}
                      className="relative w-9 h-9 rounded-full border border-[var(--border-soft)] shadow-sm -ml-3"
                      style={{ backgroundColor: c.hex }}
                    >
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="block w-[140%] h-px bg-red-500/85 rotate-45" />
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="p-10 border border-[var(--border-soft)] h-full text-center bg-white">
                <RegistryIcon className="w-16 h-16 mx-auto mb-5 text-[var(--sage-deep)]" />
                <p className="eyebrow mb-3">{t.details.registry.title}</p>
                <h3 className="font-serif text-2xl text-[var(--text-primary)] mb-4">
                  {t.details.registry.value}
                </h3>
                <p
                  data-testid="registry-public-note"
                  className="text-sm text-[var(--text-secondary)] leading-relaxed"
                >
                  {t.details.registry.note}
                </p>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div
                data-testid="adults-only-notice"
                className="p-10 border border-[var(--border-soft)] h-full text-center bg-white flex flex-col"
              >
                <AdultsOnlyIcon className="w-16 h-16 mx-auto mb-5 text-[var(--sage-deep)]" />
                <h3 className="font-serif text-2xl text-[var(--text-primary)] mb-4">{t.details.adultsOnly.title}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-5 font-light">
                  {t.details.adultsOnly.body}
                </p>
                <p className="mt-auto font-serif italic text-[var(--text-primary)] text-base">
                  {t.details.adultsOnly.thanks}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RECOMMENDED HOTELS */}
      <section
        id="hotels"
        data-testid="hotels-section"
        className="py-24 md:py-32 px-6 md:px-10 bg-[var(--ivory)] border-t border-[var(--border-soft)]"
      >
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <Hotel className="w-12 h-12 mx-auto mb-5 text-[var(--sage-deep)]" strokeWidth={1.3} />
              <SectionEyebrow>{t.details.hotel.title}</SectionEyebrow>
              <h2 className="section-title mb-5">{t.details.hotel.value}</h2>
              <p className="text-[var(--text-secondary)] max-w-2xl mx-auto text-sm md:text-base leading-relaxed font-light">
                {t.details.hotel.note}
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <ul
              data-testid="hotels-list"
              className="grid md:grid-cols-2 gap-x-12 gap-y-6 bg-white border border-[var(--border-soft)] p-8 md:p-12"
            >
              {t.accommodation.hotels.map((h) => (
                <li
                  key={h.name}
                  className="flex flex-col border-b border-dashed border-[var(--border-soft)] pb-5"
                >
                  {h.url ? (
                    <a
                      data-testid={`hotel-link-${h.name}`}
                      href={h.url}
                      target="_blank"
                      rel="noreferrer"
                      className="font-serif text-lg text-[var(--sage-deep)] hover:text-[var(--text-primary)] hover:underline"
                    >
                      {h.name} →
                    </a>
                  ) : (
                    <span className="font-serif text-lg">{h.name}</span>
                  )}
                  <span className="text-xs md:text-sm text-[var(--text-secondary)] leading-relaxed mt-2 font-light">
                    {h.note}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* TRAVEL INFORMATION */}
      <section id="travel" data-testid="travel-section" className="py-24 md:py-36 px-6 md:px-10 bg-white">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <SectionEyebrow>{t.travel.eyebrow}</SectionEyebrow>
              <h2 className="section-title">{t.travel.title}</h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Airport */}
            <Reveal>
              <div className="p-8 md:p-10 border border-[var(--border-soft)] bg-[var(--ivory)] h-full">
                <div className="flex items-center gap-3 mb-5">
                  <Plane className="w-6 h-6 text-[var(--sage-deep)]" />
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">{t.travel.airport.title}</h3>
                </div>
                <p className="text-[var(--text-secondary)] leading-relaxed text-sm md:text-base font-light">
                  {t.travel.airport.body}
                </p>
              </div>
            </Reveal>

            {/* Transport */}
            <Reveal delay={80}>
              <div className="p-8 md:p-10 border border-[var(--border-soft)] bg-[var(--ivory)] h-full">
                <div className="flex items-center gap-3 mb-5">
                  <Car className="w-6 h-6 text-[var(--sage-deep)]" />
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">{t.travel.transport.title}</h3>
                </div>
                <div className="space-y-4">
                  {t.travel.transport.items.map((it) => (
                    <div key={it.name}>
                      <p className="eyebrow text-[10px] mb-1.5">{it.name}</p>
                      <p className="text-[var(--text-secondary)] leading-relaxed text-sm font-light">{it.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Currency */}
            <Reveal delay={40}>
              <div className="p-8 md:p-10 border border-[var(--border-soft)] bg-[var(--ivory)] h-full">
                <div className="flex items-center gap-3 mb-5">
                  <Wallet className="w-6 h-6 text-[var(--sage-deep)]" />
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">{t.travel.currency.title}</h3>
                </div>
                <p className="text-[var(--text-secondary)] leading-relaxed text-sm md:text-base font-light">
                  {t.travel.currency.body}
                </p>
              </div>
            </Reveal>

            {/* Weather */}
            <Reveal delay={120}>
              <div className="p-8 md:p-10 border border-[var(--border-soft)] bg-[var(--ivory)] h-full">
                <div className="flex items-center gap-3 mb-5">
                  <CloudSun className="w-6 h-6 text-[var(--sage-deep)]" />
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">{t.travel.weather.title}</h3>
                </div>
                <p className="text-[var(--text-secondary)] leading-relaxed text-sm md:text-base font-light">
                  {t.travel.weather.body}
                </p>
              </div>
            </Reveal>

            {/* Language */}
            <Reveal>
              <div className="p-8 md:p-10 border border-[var(--border-soft)] bg-[var(--ivory)] h-full">
                <div className="flex items-center gap-3 mb-5">
                  <Languages className="w-6 h-6 text-[var(--sage-deep)]" />
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">{t.travel.language.title}</h3>
                </div>
                <p className="text-[var(--text-secondary)] leading-relaxed text-sm md:text-base font-light">
                  {t.travel.language.body}
                </p>
              </div>
            </Reveal>

            {/* Emergency */}
            <Reveal delay={80}>
              <div className="p-8 md:p-10 border border-[var(--border-soft)] bg-[var(--ivory)] h-full">
                <div className="flex items-center gap-3 mb-5">
                  <Phone className="w-6 h-6 text-[var(--sage-deep)]" />
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">{t.travel.emergency.title}</h3>
                </div>
                <p className="text-[var(--text-secondary)] leading-relaxed text-sm md:text-base font-light">
                  {t.travel.emergency.body}
                </p>
              </div>
            </Reveal>

            {/* Local Tips */}
            <Reveal delay={80}>
              <div className="p-10 md:p-14 border border-[var(--border-soft)] bg-white h-full">
                <div className="flex items-center gap-3 mb-6">
                  <Info className="w-6 h-6 text-[var(--sage-deep)]" />
                  <h3 className="font-serif text-2xl text-[var(--text-primary)]">{t.travel.tips.title}</h3>
                </div>
                <ul className="space-y-2.5 text-[var(--text-secondary)] leading-[1.55] text-sm md:text-[15px] font-light">
                  {t.travel.tips.items.map((tip, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-[var(--sage-deep)] mt-2 h-1 w-1 rounded-full bg-[var(--sage-deep)] shrink-0" />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Note from the couple */}
            <Reveal delay={120}>
              <div className="p-10 md:p-14 bg-[var(--sage-soft)]/40 border border-[var(--sage-soft)] text-center h-full flex flex-col">
                <HeartHandshake className="w-8 h-8 text-[var(--sage-deep)] mx-auto mb-5" />
                <p className="eyebrow mb-3">A & E</p>
                <h3 className="font-serif text-2xl md:text-3xl text-[var(--text-primary)] mb-6">{t.travel.note.title}</h3>
                <p className="text-[var(--text-secondary)] leading-[1.7] text-sm md:text-[15px] font-light">
                  {t.travel.note.body}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" data-testid="gallery-section" className="py-24 md:py-36 px-6 md:px-10 bg-[var(--ivory)]">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <SectionEyebrow>{t.gallery.eyebrow}</SectionEyebrow>
              <h2 className="section-title">{t.gallery.title}</h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {GALLERY_IMGS.map((src, i) => (
              <Reveal key={src} delay={i * 50}>
                <button
                  type="button"
                  data-testid={`gallery-img-${i}`}
                  onClick={() => setLightboxIdx(i)}
                  className="block w-full aspect-square overflow-hidden bg-[var(--ivory)] cursor-zoom-in group"
                  aria-label={`Open photo ${i + 1}`}
                >
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onLoad={(e) => e.currentTarget.classList.add("is-loaded")}
                    className="gallery-img w-full h-full object-cover opacity-0 transition-[opacity,transform] duration-700 ease-out group-hover:scale-[1.03]"
                    style={{ filter: "brightness(1.05) contrast(1.04) saturate(1.08)" }}
                  />
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RSVP - Invitation only */}
      <section id="rsvp" data-testid="rsvp-section" className="py-24 md:py-36 px-6 md:px-10 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <Reveal>
            <SectionEyebrow>{t.rsvp.eyebrow}</SectionEyebrow>
            <h2 className="section-title mb-6">{t.rsvp.title}</h2>
            <p className="text-[var(--text-secondary)] text-sm md:text-base tracking-wide leading-relaxed mb-3">
              {t.rsvp.subtitle}
            </p>
            <div className="hairline w-12 bg-[var(--sage-deep)] mx-auto my-8" />
            <p className="text-[var(--text-secondary)] text-sm md:text-base leading-relaxed font-light max-w-lg mx-auto">
              {t.rsvp.inviteOnly}
            </p>
            <div className="hairline w-12 bg-[var(--sage-soft)] mx-auto my-10" />
            <h3
              data-testid="rsvp-closing-title"
              className="font-serif text-2xl md:text-3xl text-[var(--text-primary)] mb-5"
            >
              {t.rsvp.closingTitle}
            </h3>
            <p
              data-testid="rsvp-closing"
              className="font-serif italic text-base md:text-lg text-[var(--text-primary)] leading-relaxed max-w-xl mx-auto"
            >
              {t.rsvp.closingMessage}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        data-testid="footer"
        className="bg-[var(--text-primary)] text-[var(--ivory)] py-20 px-6 text-center"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/2mj9rj9e_Monogram_F9F6EF.png"
          alt="A&E monogram"
          className="h-20 md:h-24 w-auto mx-auto mb-6 object-contain"
        />
        <p className="text-sm md:text-base font-serif italic mb-5">{t.footer.message}</p>
        <p className="font-serif text-xl md:text-2xl tracking-[0.22em] text-[var(--ivory)]/90">
          {t.footer.date}
        </p>
        <div className="hairline w-12 bg-[var(--ivory)]/30 mx-auto mt-8" />
      </footer>

      {/* Gallery Lightbox */}
      {lightboxIdx !== null && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            data-testid="lightbox-close"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-5 right-5 md:top-7 md:right-8 text-white/85 hover:text-white p-2"
            aria-label="Close"
          >
            <X className="w-7 h-7" />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prevImg(); }}
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 md:p-4"
            aria-label="Previous"
          >
            <ChevronLeft className="w-8 h-8 md:w-10 md:h-10" />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); nextImg(); }}
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 md:p-4"
            aria-label="Next"
          >
            <ChevronRight className="w-8 h-8 md:w-10 md:h-10" />
          </button>
          <img
            data-testid="lightbox-image"
            src={GALLERY_IMGS[lightboxIdx]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] object-contain shadow-2xl"
          />
          <div className="absolute bottom-5 left-0 right-0 text-center text-white/60 text-[11px] tracking-[0.3em] uppercase">
            {lightboxIdx + 1} / {GALLERY_IMGS.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
